import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
// utils
import isDateAfter from 'src/compass/shared/util/isDateAfter';
import getAsOfBackgroundColor from 'src/compass/shared/util/getAsOfBackgroundColor';
import { formatDate, DateFormat } from 'src/compass/shared/util/casMoment';
// hooks
import { usePeriodDates } from 'src/compass/shared/hooks/usePeriodDates';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import useFirstOfMonthDateValues, { Custom_Label } from 'src/compass/application/general/hooks/useFirstOfMonthDateValues';
import { CasStackRow, CasDeleteButton, CasPanel } from 'src/compass/shared/components';
import CasDeletePopUp from 'src/compass/shared/components/CasDeletePopUp/CasDeletePopUp';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import { useDialogDataUpdate } from 'src/compass/shared/components/CasTable/CasCRUDTable';

import { findNearestAsofDate } from '../../utils/findNearestAsofDate';
// components
import CasAsOf from './_internal/CasAsOf';
import CasCreateAndModifyDetails from './_internal/CasCreateAndModifyDetails';
import { CasAsOfMode } from './_internal/CasAsOfMode';

export interface ICasAsofContainer {
  entityTitle: string;
  children: ReactNode;
  entityId: number;
  deleteChildEntity: string;
  readOnly: boolean;
  initialAsOf: any;
  initialAsOfList: any[];
  includeCustomOption?: boolean;
  // When provided, the "add new As of" picker uses exactly these dates (YMD)
  // instead of the default first-of-month list. Already-used dates are excluded.
  newAsOfDateOptions?: string[];
  // When provided (YMD), fields are read-only for AsOf dates before this date
  // instead of before the current commission period start.
  readOnlyBeforeDate?: string;
  setReadOnly: React.Dispatch<React.SetStateAction<boolean>>;
}

function useCurrentAsOf(initialAsOf: any) {
  const [currentAsOf, setCurrentAsOf] = useState(initialAsOf);
  const { setValue, getValues, clearErrors } = useFormContext();

  const replaceFormAsOf = useCallback(
    (asof: any) => {
      setValue('asOf', asof);
      setCurrentAsOf(asof);
      clearErrors(); // Clear errors when asOf is replaced
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue]
  );

  const changeFormAsOf = useCallback(
    (partialAsOf: any) => {
      const previous = getValues('asOf') || {};
      const changed = {
        ...previous,
        ...partialAsOf,
      };

      replaceFormAsOf(changed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, getValues]
  );

  const clearNestedIdAndAsOfId = (item: Record<string, any>) => {
    const newItem = { ...item };
    if (Object.hasOwn(item, 'id')) delete newItem.id;

    Object.keys(newItem).forEach((itemKey) => {
      if (itemKey.endsWith('AsOfId')) delete newItem[itemKey];
    });
    return newItem;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const clearFormAsOfId = useCallback(() => {
    const previous = getValues('asOf') || {};
    const newAsOf = { ...previous };
    delete newAsOf.id;

    // delete id & *AsOfId (insAgencyAsOfId) from Array fields like asOfSplits/asOfTrails
    Object.entries(newAsOf).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        newAsOf[key] = value.map(clearNestedIdAndAsOfId);
      }
    });

    replaceFormAsOf(newAsOf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, getValues]);

  return {
    currentAsOf,
    changeFormAsOf,
    replaceFormAsOf,
    clearFormAsOfId,
  };
}

export default function CasAsOfContainer({
  entityTitle,
  children,
  deleteChildEntity,
  entityId,
  initialAsOf,
  initialAsOfList,
  readOnly,
  includeCustomOption = true,
  newAsOfDateOptions,
  readOnlyBeforeDate,
  setReadOnly,
}: ICasAsofContainer) {
  useEffect(() => {
    replaceFormAsOf(initialAsOf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAsOf]);

  // We need local AsOfList state because it can change after asof delete
  const [asOfList, setAsOfList] = useState(initialAsOfList || []);

  const { currentAsOf, changeFormAsOf, replaceFormAsOf, clearFormAsOfId } =
    useCurrentAsOf(initialAsOf);
  const { onDialogUpdatedData } = useDialogDataUpdate();

  const { data, isLoading } = usePeriodDates();
  const { currentPeriodStart } = data;
  const deleteDialog = useCasBoolean();

  const [mode, setMode] = useState<CasAsOfMode | null>(null);

  const [dateList, setDateList] = useState<string[] | []>([]);

  const generatedDateOptions = useFirstOfMonthDateValues({ excludeDates: dateList, includeCustomOption });
  const dateOptions = newAsOfDateOptions
    ? newAsOfDateOptions.filter((date) => !(dateList as string[]).includes(date))
    : generatedDateOptions;

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  // state will work when cancel is pressed as change in mode calls the function to find nearest as of date.
  const [showLastAsOf, setShowLastAsOf] = useState(false);

  // with thos, selected option will have the api data, otherwise it will be {label: date, value: date}
  useEffect(() => {
    if (mode === CasAsOfMode.ViewEdit) {
      const asof = asOfList.find((list: any) => list.asOfDate === selectedDate);
      replaceFormAsOf(asof);
    } else {
      changeFormAsOf({ asOfDate: selectedDate });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // will add data to asof list based on api response, alos help in deciding the mode.
  useEffect(() => {
    if (!entityId) setMode(CasAsOfMode.NewEntity);
    else if (asOfList.length === 0) {
      setDateList([]);
      setMode(CasAsOfMode.NewAsOf);
    } else {
      setDateList(asOfList.map((asOf: any) => asOf.asOfDate));
      setMode(CasAsOfMode.ViewEdit);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOfList]);

  // check for fields are editable or not.
  // Some entities use a different read-only cutoff than the commission period
  // (e.g. LRP Types use the start of the current July-to-June year).
  const readOnlyThreshold = readOnlyBeforeDate ?? currentPeriodStart;
  useEffect(() => {
    if (
      selectedDate &&
      selectedDate !== Custom_Label &&
      isDateAfter(readOnlyThreshold, selectedDate)
    ) {
      setReadOnly(true);
    } else setReadOnly(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, readOnlyThreshold]);

  useEffect(() => {
    // Find the default option based on the provided criteria
    if (mode === CasAsOfMode.ViewEdit && !showLastAsOf) {
      const nearestAsOfDate = findNearestAsofDate(dateList, currentPeriodStart);
      setSelectedDate(nearestAsOfDate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currentPeriodStart, dateList]);

  useEffect(() => {
    //  Mode changed/initialized

    //  assign first date of start 6 months for new entity & new as of
    if (mode !== CasAsOfMode.ViewEdit && mode !== null) {
      setDateList(dateOptions as string[]);
      setSelectedDate(dateOptions[0] as string);

      //  We are changing to a new asof mode - ensure asof Id info is cleared
      clearFormAsOfId();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleRemoveFromDateList = () => {
    setAsOfList((prevAsOfList: any) =>
      prevAsOfList.filter((list: any) => list?.asOfDate !== selectedDate)
    );
  };

  return (
    <CasPanel color={getAsOfBackgroundColor(readOnly, mode)}>
      {isLoading || !selectedDate ? (
        <CasLoader />
      ) : (
        <>
          <CasAsOf
            mode={mode}
            setMode={setMode}
            asOfList={asOfList}
            dateList={dateList}
            setDateList={setDateList}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            setShowLastAsOf={setShowLastAsOf}
          />

          {children}

          <CasStackRow alignItems="center" justifyContent="space-between" gapSize="none">
            {currentAsOf?.id && !readOnly && (
              <CasDeleteButton
                deleteLabelSuffix={formatDate(currentAsOf.asOfDate, DateFormat.MDY)}
                onClick={deleteDialog.setTrue}
              />
            )}
            {asOfList.length > 0 && mode !== CasAsOfMode.NewAsOf && (
              <CasCreateAndModifyDetails selectedAsOf={currentAsOf} />
            )}
          </CasStackRow>
        </>
      )}
      {deleteDialog.value && (
        <CasDeletePopUp
          entity={deleteChildEntity}
          entityTitle={entityTitle}
          id={currentAsOf.id}
          onDeleted={() => {
            deleteDialog.setFalse();
            handleRemoveFromDateList();
            onDialogUpdatedData();
          }}
          open={deleteDialog.value}
          onClose={() => deleteDialog.setFalse()}
        />
      )}
    </CasPanel>
  );
}
